import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Pencil,
  Trash2,
  UploadCloud,
  MapPin,
  Briefcase,
  Mail,
  Building,
  Calendar,
  Clock,
  AlertCircle,
} from "lucide-react";
import OrganizationClient, { OrganizationProfile } from "../data/api/OrganizationClient";
import Loader from "../../../shared/components/Loader";
import "../organizations.css";

const OrganizationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [organization, setOrganization] = useState<OrganizationProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const fetchOrganization = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await OrganizationClient.getOrganization(id);
      setOrganization(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || "Failed to load organization");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganization();
  }, [id]);

  const handleDelete = async () => {
    if (!organization?.organization_id || !id) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete "${organization.name}"? This action cannot be undone.`
    );
    if (!confirmed) return;

    setDeleting(true);
    try {
      await OrganizationClient.deleteOrganization(id);
      navigate("/dashboard/organizations");
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || "Failed to delete organization");
      setDeleting(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !organization?.organization_id) return;
    setUploading(true);
    setUploadError(null);
    try {
      const updated = await OrganizationClient.uploadLogo(
        organization.organization_id,
        file,
        organization.logo_s3_key
      );
      setOrganization((prev) => ({ ...(prev as OrganizationProfile), ...updated }));
    } catch (err: any) {
      setUploadError(
        err.response?.data?.detail || err.message || "Failed to upload logo"
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "—";
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return value;
    }
  };

  return (
    <motion.div
      className="learner-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <Loader isLoading={loading} message="Loading organization..." />

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <button
          className="org-btn"
          onClick={() => navigate("/dashboard/organizations")}
          style={{ width: 36, height: 36, padding: 0, justifyContent: "center" }}
        >
          <ArrowLeft size={18} />
        </button>
        <h1
          style={{
            fontSize: "1.25rem",
            fontWeight: 700,
            color: "var(--text-heading)",
            letterSpacing: "-0.3px",
            lineHeight: 1,
            margin: 0,
          }}
        >
          Organization Details
        </h1>
      </div>

      {error && !organization ? (
        <div className="org-detail-card">
          <div
            className="form-error"
            style={{ display: "flex", alignItems: "center", gap: 8, padding: 12 }}
          >
            <AlertCircle size={16} />
            {error}
          </div>
        </div>
      ) : organization ? (
        <>
          <div className="org-detail-card">
            <div className="org-detail-header">
              {organization.logo_url ? (
                <img
                  className="org-detail-logo"
                  src={organization.logo_url}
                  alt={`${organization.name} logo`}
                />
              ) : (
                <div className="org-detail-logo-placeholder">
                  <Building2 size={32} />
                </div>
              )}

              <div className="org-detail-info">
                <h1 className="org-detail-title">{organization.name}</h1>
                <div className="org-detail-subtitle">{organization.email}</div>
                <div className="org-detail-chips">
                  {organization.industry && (
                    <span className="org-chip">
                      <Briefcase size={12} />
                      {organization.industry}
                    </span>
                  )}
                  {organization.location && (
                    <span className="org-chip">
                      <MapPin size={12} />
                      {organization.location}
                    </span>
                  )}
                  {organization.company_name && (
                    <span className="org-chip">
                      <Building size={12} />
                      {organization.company_name}
                    </span>
                  )}
                </div>
              </div>

              <div className="org-detail-actions">
                <button
                  className="org-btn org-btn-primary"
                  onClick={() => navigate(`/dashboard/organizations/edit/${organization.organization_id}`)}
                >
                  <Pencil size={15} />
                  Edit
                </button>
                <button
                  className="org-btn org-btn-danger"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  <Trash2 size={15} />
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="form-error"
                style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, marginBottom: 16 }}
              >
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <div className="org-detail-sections">
              <div className="org-detail-section">
                <div className="org-detail-section-title">
                  <Mail size={14} />
                  Contact Information
                </div>
                <div className="org-detail-field">
                  <span className="org-detail-field-label">Contact Name</span>
                  <span className="org-detail-field-value">
                    {organization.contact?.name || "—"}
                  </span>
                </div>
                <div className="org-detail-field">
                  <span className="org-detail-field-label">Contact Phone</span>
                  <span className="org-detail-field-value">
                    {organization.contact?.phone || "—"}
                  </span>
                </div>
                <div className="org-detail-field">
                  <span className="org-detail-field-label">Contact Email</span>
                  <span className="org-detail-field-value">
                    {organization.contact?.email || "—"}
                  </span>
                </div>
              </div>

              <div className="org-detail-section">
                <div className="org-detail-section-title">
                  <Building2 size={14} />
                  Organization Info
                </div>
                <div className="org-detail-field">
                  <span className="org-detail-field-label">Company Name</span>
                  <span className="org-detail-field-value">
                    {organization.company_name || "—"}
                  </span>
                </div>
                <div className="org-detail-field">
                  <span className="org-detail-field-label">Industry</span>
                  <span className="org-detail-field-value">
                    {organization.industry || "—"}
                  </span>
                </div>
                <div className="org-detail-field">
                  <span className="org-detail-field-label">Location</span>
                  <span className="org-detail-field-value">
                    {organization.location || "—"}
                  </span>
                </div>
              </div>
            </div>

            <div className="org-detail-timestamps">
              <span className="org-timestamp">
                <Calendar size={14} />
                Created: {formatDate(organization.created_at)}
              </span>
              <span className="org-timestamp">
                <Clock size={14} />
                Last Updated: {formatDate(organization.updated_at)}
              </span>
            </div>

            <div className="org-logo-section">
              {organization.logo_url ? (
                <img
                  className="org-logo-preview"
                  src={organization.logo_url}
                  alt={`${organization.name} logo`}
                />
              ) : (
                <div className="org-logo-placeholder">
                  <UploadCloud size={24} />
                </div>
              )}
              <div className="org-logo-info">
                <p>{organization.logo_url ? "Organization Logo" : "No logo uploaded"}</p>
                <span>Upload a logo image (PNG, JPG, SVG).</span>
                {uploadError && (
                  <div style={{ color: "#dc2626", fontSize: "0.78rem", marginTop: 4 }}>
                    {uploadError}
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="org-logo-input"
                onChange={handleLogoUpload}
              />
              <button
                type="button"
                className="org-logo-upload-btn"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadCloud size={15} />
                {uploading ? "Uploading..." : "Upload Logo"}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </motion.div>
  );
};

export default OrganizationDetailPage;